import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const ConstraintSchema = z.object({
  kind: z.enum(["restaurant", "activity", "cafe", "bakery", "shop", "museum", "park", "viewpoint", "hotel"]),
  cuisine: z.string().max(40).optional(),
  activityType: z.string().max(40).optional(),
  query: z.string().min(1).max(160),
  source: z.enum(["text"]),
});

const ExpandedAreaSchema = z.object({
  label: z.string().min(1).max(160),
  localities: z.array(z.string().min(1).max(120)).max(6).default([]),
  center: z
    .object({
      lat: z.number().min(-90).max(90).finite(),
      lng: z.number().min(-180).max(180).finite(),
    })
    .optional(),
  radiusMeters: z.number().int().min(500).max(25000),
});

const AvailabilityInput = z.object({
  places: z.array(z.string().min(2).max(160)).max(6).default([]),
  constraints: z.array(ConstraintSchema).max(6).default([]),
  /** Texto original do pedido: entra na requestSignature (nunca geometria). */
  requestText: z.string().max(600).default(""),
  /**
   * Seleções fixas ("Escolher este local"): apenas identidade. O servidor
   * re-confirma placeId, coordenadas, tipos e restrição via Place Details.
   */
  pinnedSelections: z
    .array(
      z.object({
        requestSignature: z.string().min(1).max(80),
        constraintId: z.string().min(1).max(80),
        placeId: z.string().min(1).max(200),
        /** Referência curta assinada pelo servidor (HMAC + prazo de 10 min). */
        selectionRef: z.string().min(10).max(2000),
      }),
    )
    .max(6)
    .default([]),
  city: z.string().min(1).max(160),
  requestedLocality: z.string().min(1).max(160).optional(),
  requestedLocalityLabel: z.string().min(1).max(160).optional(),
  nearbyLocalities: z.array(z.string().min(1).max(120)).max(6).default([]),
  expandedArea: ExpandedAreaSchema.nullish(),
  chosenLocality: z
    .object({
      id: z.string().max(200).nullish(),
      label: z.string().min(1).max(200),
      countryCode: z.string().max(4).default(""),
      lat: z.number().min(-90).max(90).finite(),
      lng: z.number().min(-180).max(180).finite(),
    })
    .nullish(),
  lat: z.number().min(-90).max(90).finite().optional(),
  lng: z.number().min(-180).max(180).finite().optional(),
  language: z.string().min(2).max(10).default("pt"),
  day: z.number().int().min(0).max(6),
  startMinutes: z.number().int().min(0).max(1440),
  endMinutes: z.number().int().min(0).max(1440),
  minimumMinutes: z.number().int().min(5).max(600).optional(),
});

export const checkRequiredPlacesAvailability = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => AvailabilityInput.parse(data))
  .handler(async ({ data }) => {
    const { checkPlacesAvailability } = await import("./place-availability.server");
    return checkPlacesAvailability(data);
  });
